import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import "dayjs/locale/ar";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);

// Persisted timestamps (sellDate, createdAt, ...) are stored as UTC wall-clock
// values but the API echoes them back WITHOUT a timezone suffix, so a plain
// dayjs(value) parses the UTC digits as if they were local and shows the wrong
// clock (e.g. a 7:00 PM UTC+6 sale renders as 12:59 PM). Render stored
// timestamps through toLocal() so the UTC instant is converted to the viewer's
// local zone. Robust whether or not the string carries a trailing "Z".
// The business's fixed timezone (IANA, e.g. "Asia/Kuwait"), set once from company
// settings via setStoreTimeZone(). While unset, timestamps fall back to the viewer's
// device zone (the previous behavior), so there is zero change until it's configured.
//
// Seeded synchronously from localStorage at module load, NOT left null until the
// settings request resolves. Company settings arrive one round-trip after the app
// mounts, and anything that rendered a timestamp before then silently fell back to
// the device zone — so a hard reload straight onto a receipt or a POS reprint could
// print a different time than the same document printed a second later. The stored
// zone changes about never, so last session's value is the right first guess.
const STORE_TZ_KEY = "storeTimeZone";

const readPersistedTz = () => {
  try {
    return window.localStorage.getItem(STORE_TZ_KEY) || null;
  } catch {
    // Private mode / storage disabled — fall back to device zone as before.
    return null;
  }
};

let _storeTz = readPersistedTz();

export const setStoreTimeZone = (tz) => {
  _storeTz = tz || null;
  try {
    if (_storeTz) window.localStorage.setItem(STORE_TZ_KEY, _storeTz);
    else window.localStorage.removeItem(STORE_TZ_KEY);
  } catch {
    /* storage unavailable — in-memory value still applies for this session */
  }
};

// The timezone to send to the backend for server-rendered date filtering / report PDFs:
// the configured store zone if set, else the device's own IANA zone.
export const storeTimeZone = () => _storeTz || dayjs.tz.guess();

// Render a stored UTC timestamp in a FIXED business timezone, independent of the viewing
// device. Pass an explicit tzId, or omit it to use the app-wide store timezone. Falls back
// to the device-local zone when neither is available (previous behavior) or the id is bad.
export const toStoreTime = (value, tzId) => {
  const zone = tzId || _storeTz;
  if (zone) {
    try {
      return dayjs.utc(value).tz(zone);
    } catch {
      /* invalid tz id → fall through to device-local */
    }
  }
  return dayjs.utc(value).local();
};

// Back-compat: every screen that shows a stored UTC timestamp funnels through toLocal, so
// once a store timezone is configured the whole app renders in it (else device-local).
export const toLocal = (value) => toStoreTime(value);

export default dayjs;
